import { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useToast,
} from '@chakra-ui/react';

import { useTranslation } from 'helpers/languages/translations';
import ColorPicker from 'pages/Preferences/components/molecules/ColorsTab/ColorPicker';
import { DEFAULT_COLOR } from 'pages/Preferences/components/molecules/ColorsTab/useColors';
import { TClusterViewer } from './ClusterContainer';
import { TCluster } from 'pages/ClusterManagement/types';
import {
    TClusterError,
    TClusterSuccess,
    useAddClusterMutation,
    useDeleteClusterMutation,
    useEditClusterMutation,
} from 'core/API/clusterApi';

type TClusterAddModal = {
    isOpen: boolean;
    onClose: () => void;
    actionModalType: TClusterViewer['actionModalType'];
    setActionModalType: TClusterViewer['setActionModalType'];
    currentCluster?: TClusterViewer['currentCluster'];
};

const ClusterActionModal = ({
    isOpen,
    onClose,
    actionModalType,
    setActionModalType,
    currentCluster,
}: TClusterAddModal) => {
    const translate = useTranslation();
    const toast = useToast();
    const inputLabelRef = useRef<any>(null);
    const [clusterColor, setClusterColor] = useState(currentCluster?.color || DEFAULT_COLOR);

    const [addClusterMutation, { isLoading }] = useAddClusterMutation();
    const [editClusterMutation, { isLoading: isEditLoading }] = useEditClusterMutation();
    const [deleteClusterMutation, { isLoading: isDeleteLoading }] = useDeleteClusterMutation();

    const isDelete = actionModalType === 'delete';
    const isAdd = actionModalType === 'add';

    const handleSubmit = async () => {
        const resp = await addClusterMutation({ label: inputLabelRef.current.value, color: clusterColor });
        const hasError = (resp as TClusterError).error?.data.message;
        const title = hasError || (resp as { data: TClusterSuccess }).data.message;

        toast({ status: hasError ? 'error' : 'success', title });
        !hasError && onClose();
    };

    const handleEdit = async () => {
        const payload = {
            ...currentCluster,
            label: inputLabelRef.current.value,
            color: clusterColor,
        } as TCluster;

        const resp = await editClusterMutation(payload);
        const hasError = (resp as TClusterError).error?.data.message;
        const title = hasError || (resp as { data: TClusterSuccess }).data.message;

        toast({ status: hasError ? 'error' : 'success', title });
        !hasError && onClose();

        onClose();
    };

    const handleDelete = async () => {
        const resp = currentCluster && (await deleteClusterMutation(currentCluster));
        const hasError = (resp as TClusterError)?.error?.data.message;
        const title = hasError || (resp as { data: TClusterSuccess }).data.message;

        toast({ status: hasError ? 'error' : 'success', title });
        onClose();
    };

    useEffect(() => {
        setClusterColor(currentCluster?.color || DEFAULT_COLOR);
    }, [currentCluster]);

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{translate(`cluster.${actionModalType}.header`)}</ModalHeader>
                <ModalBody>
                    {isDelete ? (
                        <Text>{translate('cluster.delete.confirm')}</Text>
                    ) : (
                        <>
                            <Flex alignItems="center" mb={3}>
                                <Text w="20%">{translate('cluster.label')}</Text>
                                <Input
                                    size="sm"
                                    ref={inputLabelRef}
                                    defaultValue={isAdd ? '' : currentCluster?.label}
                                />
                            </Flex>
                            <ColorPicker colorToSubmit={{ color: clusterColor }} changeColor={setClusterColor} />
                        </>
                    )}
                </ModalBody>
                <ModalFooter gap={2}>
                    {!isDelete && !isAdd && (
                        <Button colorScheme="red" onClick={() => setActionModalType('delete')}>
                            {translate('cluster.delete')}
                        </Button>
                    )}
                    <Box flexGrow={1} />
                    <Button colorScheme="gray" onClick={onClose}>
                        {translate('cluster.cancel')}
                    </Button>
                    <Button colorScheme="blue" onClick={isDelete ? handleDelete : isAdd ? handleSubmit : handleEdit}>
                        {translate(`cluster.${actionModalType}Modal.onSubmit`)}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ClusterActionModal;
